import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid/non-secure'
import { Float32BufferAttribute, Group, type Line } from 'three'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawEntityCommand } from '@/plugins/cad/module/CADModule/command/DrawEntityCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { MTextEntity } from '@/plugins/cad/module/CADModule/model/entity'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import { KeyCodes } from '@/shared/constants'

const textMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryMTextInput: () => {},
    entryFinished: () => {},
  },
}).createMachine({
  id: 'drawTextTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: 'draw',
      },
    },
    draw: {
      initial: 'mText',
      states: {
        mText: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.text.bounds },
            },
            point2: {
              on: { NEXT: 'input' },
              entry: { type: 'showMouseTip', params: CadTipKey.draw.text.bounds },
            },
            input: {
              on: {
                MOUSE_RIGHT_DOWN: '#drawTextTool.finished',
                KEY_DOWN_ENTER: '#drawTextTool.finished',
              },
              entry: 'entryMTextInput',
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 多行文字绘制工具，拾取矩形文字框后弹出输入框完成编辑。
 */
@injectable()
export class DrawTextTool extends StateMachineTool<typeof textMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawTextTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = textMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryMTextInput: () => this.entryMTextInput(),
      entryFinished: () => this.entryFinished(),
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private previewLineObject: Line | null = null
  private entity: MTextEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private rectData: { point: IPoint; width: number; height: number } | null = null
  private inputElement: HTMLTextAreaElement | null = null

  protected onInitialize(entityData?: Partial<MTextEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      undefined,
      this.isCanCommit.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
    this.rectData = null
    if (this.inputElement) {
      this.inputElement.remove()
      this.inputElement = null
    }
  }

  /**
   * 鼠标按下：左键依次记录文字矩形的两个对角点，右键进入输入阶段。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint && this.points.length < 2) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.updateCanvas()
        this.send({ type: 'NEXT' })
      }
    } else if (params.event.button === 2) {
      // 右键
      this.send({ type: 'MOUSE_RIGHT_DOWN' })
    }
  }

  /**
   * 鼠标移动：更新文字矩形的预览角点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    if (this.points.length >= 2) {
      return
    }
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出，ENTER 触发状态机 KEY_DOWN_ENTER 事件。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    } else if (params.event.keyCode === KeyCodes.ENTER) {
      this.send({ type: 'KEY_DOWN_ENTER' })
    }
  }

  /** 每帧刷新文字输入框位置 */
  onViewerUpdate() {
    this.updateInputBoxPosition()
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  private entryMTextInput() {
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.clearObject()
    // 弹出文本输入框，待用户输入文本内容
    this.rectData = this.getRectData(this.points[0], this.points[1])
    this.inputElement = this.createInputBox(this.rectData)
  }

  private entryFinished() {
    this.updateEntity()
    this.exit()
  }

  private createInputBox(rect: { point: IPoint; width: number; height: number }) {
    // 将画布坐标转换为屏幕坐标
    const screenPoint = this.controller.getScreenPosition({
      x: rect.point.x,
      y: rect.point.y,
      z: 0,
    })
    const screenBRPoint = this.controller.getScreenPosition({
      x: rect.point.x + rect.width,
      y: rect.point.y + rect.height,
      z: 0,
    })
    const screenWidth = Math.abs(screenBRPoint.x - screenPoint.x)
    const screenHeight = Math.abs(screenBRPoint.y - screenPoint.y)
    const inputElement = document.createElement('textarea')
    Object.assign(inputElement.style, {
      position: 'absolute',
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
      minWidth: `${screenWidth}px`,
      maxWidth: '-webkit-fill-available',
      minHeight: `${screenHeight}px`,
      outline: 'none',
      backgroundColor: '#fff',
      color: '#000',
    })
    this.controller.domElement.parentElement?.appendChild(inputElement)
    inputElement.addEventListener('mousedown', (event) => {
      if (event.button === 2) {
        this.send({ type: 'MOUSE_RIGHT_DOWN' })
      }
    })
    inputElement.focus()
    return inputElement
  }

  private updateInputBoxPosition() {
    if (!this.inputElement || !this.rectData) {
      return
    }
    const screenPoint = this.controller.getScreenPosition({
      x: this.rectData.point.x,
      y: this.rectData.point.y,
      z: 0,
    })
    Object.assign(this.inputElement.style, {
      left: `${screenPoint.x}px`,
      top: `${screenPoint.y}px`,
    })
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private clearObject() {
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.previewLineObject = null
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }
    const [p1, p2] = points
    // 绘制辅助预览线
    this.updatePreviewLine(p1, p2)
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    const points = this.getRectPointsByTwoCorners(p1, p2)
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(20)
      const materialOption = {
        color: 0xffffff,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedPolyline(
        points,
        materialOption,
        this.group,
        true
      )
      return
    }

    const positions = points.concat([points[0]]).flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  private getRectPointsByTwoCorners(p1: IPoint, p2: IPoint): IPoint[] {
    // 顶点 1: 起点 P1
    const pt1 = { x: p1.x, y: p1.y }

    // 顶点 2: (P2.x, P1.y) -> 与 P1 水平
    const pt2 = { x: p2.x, y: p1.y }

    // 顶点 3: 对角点 P2
    const pt3 = { x: p2.x, y: p2.y }

    // 顶点 4: (P1.x, P2.y) -> 与 P1 垂直
    const pt4 = { x: p1.x, y: p2.y }

    // 返回顶点的顺序决定了绘制的方向（顺时针或逆时针取决于 P1 和 P2 的相对位置）
    return [pt1, pt2, pt3, pt4]
  }

  private getRectData(p1: IPoint, p2: IPoint) {
    const minX = Math.min(p1.x, p2.x)
    const maxX = Math.max(p1.x, p2.x)
    const minY = Math.min(p1.y, p2.y)
    const maxY = Math.max(p1.y, p2.y)

    return {
      // 左上角的点
      point: { x: minX, y: maxY },
      width: maxX - minX,
      height: maxY - minY,
    }
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<MTextEntity>) {
    const entity = new MTextEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    if (entityData?.textHeight) {
      entity.textHeight = entityData.textHeight
    }
    return entity
  }

  private updateEntity() {
    if (!this.entity || !this.inputElement || !this.rectData) {
      return
    }
    Object.assign(this.entity, {
      position: this.rectData.point,
      rectWidth: this.rectData.width,
      rectHeight: this.rectData.height,
      contents: this.inputElement.value,
    })
  }

  private isCanCommit(): boolean {
    return !!this.entity?.contents
  }
}
